import { ComponentProps, FC, ReactNode, useContext } from "react";
import { Text as RNText } from "react-native";
import * as Slot from "@rn-primitives/slot";
import { Trans, TransProps } from "react-i18next";

import { TextClassContext } from "@/context/TextClassContext";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { ARIA_LEVEL, ROLE, textVariants, TextVariantType } from "./variants";

export type VnTextPropsType = ComponentProps<typeof RNText> & {
  asChild?: boolean;
  children?: ReactNode;
  // An array is tried in order, falling back to the next key when one is missing.
  i18nKey?: I18nKeyType | I18nKeyType[];
  i18nOptions?: object;
  i18nProps?: Omit<TransProps<I18nKeyType>, "i18nKey" | "values">;
  variant?: TextVariantType;
};

export const VnText: FC<VnTextPropsType> = ({
  className,
  asChild = false,
  variant = "md",
  children,
  i18nKey,
  i18nOptions,
  i18nProps,
  ...props
}) => {
  const textClass = useContext(TextClassContext);
  const Component = asChild ? Slot.Text : RNText;

  return (
    <Component
      className={cn(textVariants({ variant }), textClass, className)}
      role={variant ? ROLE[variant] : undefined}
      aria-level={variant ? ARIA_LEVEL[variant] : undefined}
      {...props}>
      {children ??
        (i18nKey ? (
          <Trans
            // Doing this as a workaround for a typing error with i18nKey union
            i18nKey={i18nKey as string | string[]}
            values={i18nOptions}
            {...i18nProps}
            components={{
              medium: (
                <VnText
                  variant={variant}
                  className={cn(textClass, className, "font-primary-medium")}
                />
              ),
              bold: (
                <VnText
                  variant={variant}
                  className={cn(textClass, className, "font-primary-semibold")}
                />
              ),
              colorEmphasis: (
                <VnText
                  variant={variant}
                  className={cn(textClass, className, "text-sidebar")}
                />
              ),
              italic: (
                <VnText
                  variant={variant}
                  className={cn(textClass, className, "italic")}
                />
              ),
              underline: (
                <VnText
                  variant={variant}
                  className={cn(textClass, className, "underline")}
                />
              ),
              ...i18nProps?.components,
            }}
          />
        ) : null)}
    </Component>
  );
};
